import React from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { ShieldCheck, Crown, Compass, Gem, Handshake, Leaf, Briefcase, Award, Landmark, Coffee, Sparkles, HeartHandshake, ArrowRight } from 'lucide-react';

const FOUNDER_IMAGE = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_7012c185-728e-48dd-a634-a866967c4d91/artifacts/o7ztd331_20260425_003730-IMG_STYLE.jpg';

export const AboutPage = () => {
  const services = [
    {
      icon: <Gem size={48} />,
      title: 'Bespoke FIT & High-End Private Journeys',
      description: 'Tailor-made luxury itineraries • VIP airport fast-track & private transfers • Exclusive villa & 5★ luxury resort buyouts • Private yachts & helicopter charters.'
    },
    {
      icon: <Briefcase size={48} />,
      title: 'Corporate Gatherings & Strategic MICE Handling',
      description: 'Executive retreats & incentive travels • High-level conference management • Creative venue sourcing across Indonesia • End-to-end ground logistics & VIP banquet setups.'
    },
    {
      icon: <Landmark size={48} />,
      title: 'Cultural Immersions & Heritage Expeditions',
      description: 'Indonesia Coffee Civilization trails & terroir cupping • Private access to sacred temples & royal heritage sites • Master artisan workshops • Private dining with indigenous masters.'
    },
    {
      icon: <HeartHandshake size={48} />,
      title: 'Holistic Wellness, Ayurveda & Signature Celebrations',
      description: 'Curated yoga & Ayurvedic rejuvenation retreats • Destination wedding logistics • Private multi-generational family milestones • Sound healing & traditional holistic therapies.'
    }
  ];

  const values = [
    {
      icon: <ShieldCheck size={40} />,
      title: 'Uncompromising Excellence',
      description: 'Flawless execution from private charter logistics to 24/7 on-ground VIP concierge. We maintain strict service benchmarks across every destination.'
    },
    {
      icon: <Crown size={40} />,
      title: 'Bespoke Artistry',
      description: 'No cookie-cutter itineraries. Every journey is meticulously tailored to individual passions, privacy preferences, and refined culinary requirements.'
    },
    {
      icon: <Compass size={40} />,
      title: 'Deep Cultural Immersion',
      description: 'Authentic connections with indigenous heritage, sacred traditions, and master artisans, curated with local reverence and scholarly depth.'
    },
    {
      icon: <Gem size={40} />,
      title: 'Curated Luxury & Terroir',
      description: "Exclusive access to Indonesia's finest boutique sanctuaries, world-class volcanic terroirs, and private wellness pavilions inaccessible to the mass market."
    },
    {
      icon: <Handshake size={40} />,
      title: 'Trust & Professional Integrity',
      description: 'A credible, legally registered DMC partner (PT Indotravelista Jelajah Buana) offering transparent contracting, verified B2B net rates, and reliable supplier relationships.'
    },
    {
      icon: <Leaf size={40} />,
      title: 'Regenerative & Sustainable Impact',
      description: 'Committed to ethical tourism that protects fragile ecosystems, honors local communities, and preserves Indonesian cultural legacy for future generations.'
    }
  ];

  return (
    <div style={{ background: '#0B132B', overflowX: 'hidden' }}>
      <Navigation />
      <WhatsAppButton variant="floating" />

      {/* Luxury Editorial Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #0B132B 0%, #1E293B 50%, #0F172A 100%)',
        padding: '140px 0 100px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.08) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: '48px',
            fontWeight: 300,
            color: '#F8FAFC',
            marginBottom: '24px',
            letterSpacing: '2px',
            lineHeight: 1.2
          }} className="luxury-hero-title">
            About Indotravelista
          </div>
          <div style={{
            fontSize: '16px',
            color: '#D4AF37',
            fontWeight: 500,
            letterSpacing: '2px',
            marginBottom: '16px',
            textTransform: 'uppercase'
          }} className="about-subtitle">
            PT Indotravelista Jelajah Buana
          </div>
          <div style={{
            fontSize: '14px',
            color: '#C5A880',
            fontWeight: 400,
            letterSpacing: '1px',
            fontStyle: 'italic'
          }} className="about-tagline">
            Indonesia Destination Management Company
          </div>
          <div style={{
            width: '120px',
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)',
            margin: '40px auto 0'
          }} />
        </div>
      </section>

      {/* Our Story - Asymmetrical Editorial Layout */}
      <section className="section-padding" style={{ background: '#0F172A', padding: '100px 0' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 0.8fr',
            gap: '80px',
            maxWidth: '1400px',
            margin: '0 auto'
          }} className="story-grid">
            {/* Left Column - Story Narrative */}
            <div>
              <h2 style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: '48px',
                fontWeight: 300,
                color: '#F8FAFC',
                marginBottom: '40px',
                letterSpacing: '1px'
              }}>
                Our Story
              </h2>
              <div style={{
                width: '60px',
                height: '2px',
                background: '#D4AF37',
                marginBottom: '40px'
              }} />
              
              <p style={{
                fontSize: '17px',
                lineHeight: 1.9,
                color: '#E2E8F0',
                marginBottom: '28px',
                textAlign: 'left',
                letterSpacing: '0.3px'
              }} className="story-text">
                Indotravelista was established in 2008 as an online ticketing business and later expanded into customized travel experiences. The company specializes in creating private journeys, corporate events, wellness retreats, cultural immersions, and tailor-made itineraries throughout Indonesia and international destinations.
              </p>
              
              <p style={{
                fontSize: '17px',
                lineHeight: 1.9,
                color: '#E2E8F0',
                marginBottom: '40px',
                textAlign: 'left',
                letterSpacing: '0.3px'
              }} className="story-text">
                Today, Indotravelista focuses on delivering premium, authentic, and personalized travel experiences for clients from Indonesia and around the world. Our network of local partners enables us to create memorable journeys that combine culture, nature, adventure, wellness, and luxury hospitality.
              </p>
              
              {/* Gold Quote Card */}
              <div style={{
                padding: '40px',
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(197, 168, 128, 0.05) 100%)',
                border: '2px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '2px',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  left: '40px',
                  fontSize: '80px',
                  color: '#D4AF37',
                  opacity: 0.3,
                  fontFamily: 'Georgia, serif'
                }}>&ldquo;</div>
                <p style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontSize: '24px',
                  fontStyle: 'italic',
                  color: '#F8FAFC',
                  marginBottom: '12px',
                  lineHeight: 1.5,
                  position: 'relative',
                  zIndex: 2
                }}>
                  Live the Journey — Create Memories That Last Forever
                </p>
                <p style={{
                  fontSize: '14px',
                  color: '#C5A880',
                  fontWeight: 600,
                  letterSpacing: '2px',
                  textTransform: 'uppercase'
                }}>
                  Our Philosophy
                </p>
              </div>
            </div>

            {/* Right Column - Heritage Badges */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '32px',
              paddingTop: '60px'
            }}>
              <div style={{
                padding: '40px',
                background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                textAlign: 'center',
                borderRadius: '2px'
              }}>
                <div style={{
                  fontSize: '56px',
                  fontWeight: 700,
                  color: '#D4AF37',
                  marginBottom: '12px',
                  fontFamily: 'Georgia, serif'
                }}>
                  2008
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#E2E8F0',
                  letterSpacing: '2px',
                  textTransform: 'uppercase'
                }}>
                  Established
                </div>
              </div>

              <div style={{
                padding: '40px',
                background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                textAlign: 'center',
                borderRadius: '2px'
              }}>
                <div style={{
                  fontSize: '56px',
                  fontWeight: 700,
                  color: '#D4AF37',
                  marginBottom: '12px',
                  fontFamily: 'Georgia, serif'
                }}>
                  15+
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#E2E8F0',
                  letterSpacing: '2px',
                  textTransform: 'uppercase'
                }}>
                  Years of Curated Journeys
                </div>
              </div>

              <div style={{
                padding: '32px',
                background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                textAlign: 'center',
                borderRadius: '2px'
              }}>
                <div style={{
                  fontSize: '16px',
                  color: '#C5A880',
                  letterSpacing: '1px',
                  lineHeight: 1.6,
                  fontWeight: 500
                }}>
                  Bespoke Inbound<br />& Outbound<br />DMC Services
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Profile - Frameless Two-Column Layout */}
      <section className="section-padding" style={{ background: '#0B132B', padding: '100px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '48px',
              fontWeight: 300,
              color: '#F8FAFC',
              marginBottom: '20px',
              letterSpacing: '1px'
            }}>
              Our Founder
            </h2>
            <div style={{
              width: '80px',
              height: '2px',
              background: '#D4AF37',
              margin: '0 auto'
            }} />
          </div>

          <div style={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '380px 1fr',
              gap: '80px',
              alignItems: 'center'
            }} className="founder-grid">
              {/* Left - Portrait with Minimal Frame */}
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '320px',
                  height: '320px',
                  margin: '0 auto',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '3px solid #D4AF37',
                  padding: '12px',
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(197, 168, 128, 0.05))',
                  boxShadow: '0 0 60px rgba(212, 175, 55, 0.3), inset 0 0 40px rgba(212, 175, 55, 0.05)'
                }}>
                  <img
                    src={FOUNDER_IMAGE}
                    alt="Helen Stephani"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              </div>

              {/* Right - Executive Bio */}
              <div>
                <h3 style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontSize: '42px',
                  fontWeight: 400,
                  color: '#F8FAFC',
                  marginBottom: '12px',
                  letterSpacing: '0.5px'
                }}>
                  Helen Stephani
                </h3>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#B45309',
                  marginBottom: '16px',
                  letterSpacing: '1px'
                }}>
                  Founder & Managing Director
                </div>
                <div style={{
                  fontSize: '16px',
                  fontStyle: 'italic',
                  color: '#D4AF37',
                  marginBottom: '32px',
                  fontWeight: 500,
                  letterSpacing: '0.5px'
                }}>
                  Pioneering Bespoke Indonesian Inbound & Experiential Journeys
                </div>
                <div style={{
                  width: '60px',
                  height: '2px',
                  background: '#D4AF37',
                  marginBottom: '32px'
                }} />
                <p style={{
                  fontSize: '16px',
                  lineHeight: 1.9,
                  color: '#E2E8F0',
                  textAlign: 'left',
                  letterSpacing: '0.3px'
                }} className="founder-bio">
                  With extensive experience representing Indonesian tourism across international markets, Helen leads Indotravelista and LuminaVerse Media with a vision to craft transcendent, culturally immersive travel. Her cross-disciplinary expertise spans destination management, luxury hospitality curations, creative storytelling, and high-level international travel partnerships.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4-Pillar Core Services - Borderless Premium Cards */}
      <section className="section-padding" style={{ background: '#0F172A', padding: '100px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '48px',
              fontWeight: 300,
              color: '#F8FAFC',
              marginBottom: '20px',
              letterSpacing: '1px'
            }}>
              Our Core Services
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#C5A880',
              letterSpacing: '1px'
            }}>
              Four pillars of luxury destination management
            </p>
            <div style={{
              width: '80px',
              height: '2px',
              background: '#D4AF37',
              margin: '24px auto 0'
            }} />
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '32px',
            maxWidth: '1200px',
            margin: '0 auto'
          }} className="services-grid">
            {services.map((service, index) => (
              <div
                key={`service-${index}-${service.title.substring(0, 15)}`}
                className="service-pillar"
                style={{
                  padding: '48px 40px',
                  background: 'rgba(15, 23, 42, 0.4)',
                  borderRadius: '16px',
                  textAlign: 'left',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'default'
                }}
              >
                {/* Subtle gradient overlay on hover */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05), transparent)',
                  opacity: 0,
                  transition: 'opacity 0.4s ease',
                  pointerEvents: 'none',
                  zIndex: 1
                }} className="service-overlay" />
                
                <div style={{ position: 'relative', zIndex: 2 }}>
                  <div style={{
                    display: 'inline-flex',
                    padding: '16px',
                    background: 'rgba(212, 175, 55, 0.1)',
                    borderRadius: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{ color: '#D4AF37' }}>
                      {service.icon}
                    </div>
                  </div>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: 600,
                    color: '#F8FAFC',
                    marginBottom: '16px',
                    lineHeight: 1.3,
                    letterSpacing: '0.3px'
                  }}>
                    {service.title}
                  </h3>
                  <p style={{
                    fontSize: '15px',
                    lineHeight: 1.8,
                    color: '#C5A880',
                    letterSpacing: '0.2px'
                  }}>
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values - 6-Card Luxury Grid */}
      <section className="section-padding" style={{ background: '#0B132B', padding: '100px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '48px',
              fontWeight: 300,
              color: '#F8FAFC',
              marginBottom: '20px',
              letterSpacing: '1px'
            }}>
              Our Values
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#C5A880',
              letterSpacing: '1px'
            }}>
              Luxury DMC excellence for discerning travelers
            </p>
            <div style={{
              width: '80px',
              height: '2px',
              background: '#D4AF37',
              margin: '24px auto 0'
            }} />
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: '36px',
            maxWidth: '1300px',
            margin: '0 auto'
          }}>
            {values.map((value, index) => (
              <div
                key={`value-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="value-card-luxury"
                style={{
                  padding: '44px 32px',
                  background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                  border: '1px solid rgba(212, 175, 55, 0.25)',
                  borderRadius: '2px',
                  textAlign: 'center',
                  transition: 'all 0.4s ease'
                }}
              >
                <div style={{
                  color: '#D4AF37',
                  marginBottom: '24px',
                  display: 'flex',
                  justifyContent: 'center',
                  transition: 'transform 0.4s ease'
                }} className="value-icon-luxury">
                  {value.icon}
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#F8FAFC',
                  marginBottom: '16px',
                  letterSpacing: '0.5px'
                }}>
                  {value.title}
                </h3>
                <p style={{
                  fontSize: '15px',
                  lineHeight: 1.8,
                  color: '#C5A880',
                  letterSpacing: '0.3px'
                }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Luxury Concierge CTA */}
      <section style={{
        background: 'linear-gradient(135deg, #1E293B 0%, #0B132B 100%)',
        padding: '80px 0',
        borderTop: '1px solid rgba(212, 175, 55, 0.2)',
        borderBottom: '1px solid rgba(212, 175, 55, 0.2)'
      }}>
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '38px',
              fontWeight: 300,
              color: '#F8FAFC',
              marginBottom: '24px',
              letterSpacing: '1px',
              lineHeight: 1.4
            }}>
              Looking for a Customized DMC Arrangement<br />or Bespoke Itinerary?
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#C5A880',
              marginBottom: '40px',
              lineHeight: 1.7
            }}>
              Connect with our travel designers for personalized luxury experiences
            </p>
            <a
              href="https://wa.me/6281517118799?text=Hello%20Indotravelista,%20I%20would%20like%20to%20consult%20about%20a%20tailor-made%20service."
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 48px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B45309 100%)',
                color: '#0B132B',
                textDecoration: 'none',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '2px',
                letterSpacing: '1px',
                transition: 'all 0.3s ease',
                border: '1px solid #D4AF37'
              }}
              className="luxury-cta-btn"
            >
              Consult with Our Travel Designer
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap');
        
        .service-pillar:hover {
          transform: translateY(-8px);
          background: rgba(15, 23, 42, 0.6) !important;
          box-shadow: 0 12px 40px rgba(212, 175, 55, 0.15);
        }
        
        .service-pillar:hover .service-overlay {
          opacity: 1 !important;
        }
        
        .value-card-luxury:hover {
          transform: translateY(-8px);
          border-color: #D4AF37;
          box-shadow: 0 20px 60px rgba(212, 175, 55, 0.3);
        }
        
        .value-card-luxury:hover .value-icon-luxury {
          transform: scale(1.15);
        }
        
        .luxury-cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(212, 175, 55, 0.5);
          background: linear-gradient(135deg, #E5C048 0%, #C5641A 100%) !important;
        }
        
        @media (max-width: 1024px) {
          .story-grid {
            grid-template-columns: 1fr !important;
            gap: 50px !important;
          }
          
          .founder-grid {
            grid-template-columns: 1fr !important;
            gap: 50px !important;
            text-align: center !important;
          }
          
          .founder-grid > div:last-child p {
            text-align: center !important;
          }
          
          .services-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
        }
        
        @media (max-width: 768px) {
          .luxury-hero-title {
            font-size: 36px !important;
            line-height: 1.3 !important;
          }
          
          .about-subtitle {
            font-size: 13px !important;
            letter-spacing: 1.5px !important;
          }
          
          .about-tagline {
            font-size: 12px !important;
          }
          
          .story-text,
          .founder-bio {
            font-size: 15px !important;
            text-align: left !important;
          }
        }
        
        @media (max-width: 480px) {
          .luxury-hero-title {
            font-size: 32px !important;
          }
        }
      `}</style>
    </div>
  );
};
